import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "../../lib/useFetch";
import { Panel } from "./shared";

/** Xero connection panel (Phase D) — shown on the finance AND admin homes;
 *  admins are the ones who authorise the organisations. */

type XeroStatus = {
  configured: boolean;
  connections: {
    tenantName: string | null;
    brand: string | null;
    tokenExpiresAt: string;
    connectedBy: string | null;
    needsReconnect: boolean;
    lastError: string | null;
    lastErrorAt: string | null;
  }[];
};

export function XeroConnectionPanel() {
  const { fetch } = useFetch();
  const [xero, setXero] = useState<XeroStatus | null>(null);
  const [searchParams] = useSearchParams();
  const outcome = searchParams.get("xero");
  const OUTCOME_MSG: Record<string, [string, string]> = {
    connected: ["text-green-700", "Xero connected — the organisation now shows below."],
    denied: ["text-red-600", "Xero authorisation was declined."],
    "state-mismatch": ["text-red-600", "The connection attempt expired or lost its state cookie — click Connect and complete it in the same tab within 10 minutes."],
    error: ["text-red-600", "Xero connection failed server-side — check with Claude for the log detail."],
    "not-configured": ["text-red-600", "Xero credentials aren't configured on this environment."],
  };

  useEffect(() => {
    fetch<{ data: XeroStatus }>("/api/finance/xero/status")
      .then((r) => setXero(r.data))
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Panel
      title="Xero connection"
      action={
        xero?.configured ? (
          <a
            className={
              xero.connections.some((c) => c.needsReconnect)
                ? "rounded-md bg-red-600 px-2.5 py-1 text-sm font-medium text-white hover:bg-red-700"
                : "text-sm text-blue-600 hover:underline"
            }
            href="/api/finance/xero/connect"
          >
            {xero.connections.some((c) => c.needsReconnect)
              ? "Reconnect Xero"
              : xero.connections.length
                ? "Connect another org / reconnect"
                : "Connect Xero"}
          </a>
        ) : undefined
      }
    >
      {outcome && OUTCOME_MSG[outcome] && (
        <p className={`mb-2 text-sm font-medium ${OUTCOME_MSG[outcome][0]}`}>{OUTCOME_MSG[outcome][1]}</p>
      )}
      {!xero ? (
        <p className="text-sm text-slate-500">Checking…</p>
      ) : !xero.configured ? (
        <p className="text-sm text-slate-500">
          Xero client credentials aren't configured on this environment yet — payments, reminders and mark-paid stay
          read-only until they are.
        </p>
      ) : xero.connections.length === 0 ? (
        <p className="text-sm text-slate-500">
          Not connected. Connect the HELM and Anchor Xero organisations with the link above (one organisation per
          pass) — this unlocks the write paths: payment on approve, reminders, mark paid, batch pay.
        </p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {xero.connections.map((c) => (
              <tr key={c.tenantName ?? c.brand ?? "?"} className="border-t first:border-t-0">
                <td className="py-2 pr-3 font-medium">{c.tenantName ?? "—"}</td>
                <td className="py-2 pr-3 capitalize text-slate-500">{c.brand ?? "—"}</td>
                {c.needsReconnect ? (
                  <td className="py-2 text-xs font-medium text-red-600" title={c.lastError ?? undefined}>
                    Needs reconnecting — Xero rejected its token
                  </td>
                ) : (
                  <td className="py-2 text-xs text-slate-400">connected{c.connectedBy ? ` by ${c.connectedBy}` : ""}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {xero?.connections.some((c) => c.needsReconnect) && (
        <p className="mt-2 text-xs text-slate-500">
          Until it's reconnected, booking pages show each document's last synced state instead of live Xero figures.
          One reconnect re-authorises every organisation you approve.
        </p>
      )}
    </Panel>
  );
}
